import {useState, useCallback, useEffect, useRef} from 'react'
import {getListCustomShapeTemplate} from '../../../../services/ArtworkTemplateServices'
import {usePagination} from './usePagination'

const DEFAULT_LIMIT = 12
const EMPTY_FILTERS = {}

/**
 * Strips empty-string values so they aren't sent as query params.
 * @param {Record<string, string>} filters
 * @returns {Record<string, string>}
 */
function cleanFilters(filters) {
    const cleaned = {}
    for (const [key, value] of Object.entries(filters)) {
        const trimmed = typeof value === 'string' ? value.trim() : value
        if (trimmed !== '' && trimmed != null) {
            cleaned[key] = trimmed
        }
    }
    return cleaned
}

/**
 * @param {{ page?: number, limit?: number, filters?: Record<string, string>, refreshKey?: number|string, enabled?: boolean }} [options]
 * @returns {{
 *   items: object[],
 *   total: number,
 *   page: number,
 *   limit: number,
 *   filters: Record<string, string>,
 *   loading: boolean,
 *   error: Error | null,
 *   pagination: ReturnType<typeof usePagination>,
 *   loadPage: (page: number) => void,
 *   setFilters: (filters: Record<string, string>) => void,
 * }}
 */
export function useShapesTemplateList(options = {}) {
    const {
        page: initialPage = 1,
        limit = DEFAULT_LIMIT,
        filters: initialFilters = EMPTY_FILTERS,
        refreshKey,
        enabled = true,
    } = options
    const [page, setPage] = useState(initialPage)
    const [filters, setFiltersState] = useState(initialFilters)
    const [items, setItems] = useState([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [reloadNonce, setReloadNonce] = useState(0)

    const filtersKey = JSON.stringify(filters)

    const pagination = usePagination({
        totalItems: total,
        limit,
        page,
        onPageChange: setPage,
    })

    const loadPage = useCallback((pageNum) => {
        setPage(pageNum)
    }, [])

    /** Update filters and reset to page 1. */
    const setFilters = useCallback((f) => {
        setFiltersState(f)
        setPage(1)
    }, [])

    // Track previous filters so we can detect changes from outside (e.g. initialFilters prop).
    const prevFiltersKeyRef = useRef(filtersKey)
    useEffect(() => {
        if (prevFiltersKeyRef.current !== filtersKey) {
            prevFiltersKeyRef.current = filtersKey
            setPage(1)
        }
    }, [filtersKey])

    const prevRefreshKeyRef = useRef(refreshKey)
    useEffect(() => {
        if (prevRefreshKeyRef.current !== refreshKey) {
            prevRefreshKeyRef.current = refreshKey
            if (page === 1) {
                setReloadNonce((value) => value + 1)
                return
            }
            setPage(1)
        }
    }, [page, refreshKey])

    useEffect(() => {
        if (!enabled) {
            setLoading(false)
            setError(null)
            setItems([])
            setTotal(0)
            return
        }

        let cancelled = false
        setLoading(true)
        setError(null)

        const params = {page, limit, ...cleanFilters(filters)}

        ;(async () => {
            try {
                const res = await getListCustomShapeTemplate(params)
                if (cancelled) return
                if (!res.success) {
                    setError(new Error('Failed to load shapes'))
                    setItems([])
                    setTotal(0)
                    return
                }
                const data = res.data || {}
                setItems(data.designTemplate || [])
                setTotal(data.total ?? 0)
            } catch (err) {
                if (!cancelled) {
                    setError(err)
                    setItems([])
                    setTotal(0)
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [enabled, page, limit, filtersKey, reloadNonce])

    return {
        items,
        setItems,
        total,
        page,
        limit,
        filters,
        loading,
        error,
        pagination,
        loadPage,
        setFilters,
    }
}
